import {
  americanoFixtureCount,
  fixtureCount,
  seedEntrants,
  splitIntoTeams,
  validAmericanoMatches,
  validMatchesPerPlayer,
  OPEN_SEED_GAP,
  suggestedSwissRounds,
} from "@rally/draw";
import { formatRating } from "@rally/rating";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { PageHeader } from "../components/AppShell.js";
import { FixtureList } from "../components/FixtureList.js";
import { IconDown, IconUp } from "../components/icons.js";
import { offersSeeding, seedingHint } from "./event/EventSeeding.js";
import { TeamSheets } from "../components/TeamSheets.js";
import { Button, Field, IconButton, inputStyle, Notice, Selector } from "../components/ui.js";
import { hasRole, useAuth } from "../lib/auth.js";
import { rosterOf, useDraw, type Roster } from "./event/shared.js";
import { closesWhen } from "./event/EventRegister.js";
import { fetchDraw, generateDraw, isPlayable } from "../lib/draw.js";
import { swissRoundChoices, swissRoundDefault } from "../lib/swiss.js";
import { fetchTieTeams, setTieTeams } from "../lib/tie.js";
import {
  closeEventEntries,
  fetchRatings,
  fetchTournaments,
  findEvent,
  reopenEventEntries,
  updateEventDescription,
  updateEventDetails,
} from "../lib/tournaments.js";
import {
  areEntriesClosed,
  describeEvent,
  EVENT_FORMAT_LABEL,
  PAIRING_LABEL,
  registeredEntries,
  type EventFormat,
  type TiePairing,
  type Tournament,
  type TournamentEvent,
} from "../lib/types.js";
import { PoolStepRows } from "./OrganiseRows.js";

/**
 * The organiser's console for one event.
 *
 * Separate from the event page on purpose. An organiser is also a player — they register for
 * their own club's events — and putting "redraw everything" beside "register me" makes the
 * destructive button harder to avoid and the personal one harder to find. It also means the
 * link an organiser sends to the club is the same link they use themselves, with the
 * controls simply absent for everyone else.
 *
 * The role check here is a routing convenience, not a defence. Every write below is refused
 * by the database for anybody who is not an organiser, whether or not this page renders.
 */
export function EventManage() {
  const { eventId } = useParams<{ eventId: string }>();
  const { member } = useAuth();

  const {
    data: tournaments,
    isPending,
    error,
  } = useQuery({ queryKey: ["tournaments"], queryFn: fetchTournaments });

  if (!hasRole(member, "organiser")) return <Navigate to={`/events/${eventId}`} replace />;

  const found = eventId && tournaments ? findEvent(tournaments, eventId) : null;

  if (isPending) {
    return (
      <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
        <p className="micro">Loading…</p>
      </main>
    );
  }

  if (error || !found) {
    return (
      <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
        <Notice tone="attention">
          {error ? "Couldn't load this event. Try again." : "That event isn't here any more."}
        </Notice>
      </main>
    );
  }

  const { event, tournament } = found;

  return <ManageDetail event={event} tournament={tournament} />;
}

function ManageDetail({ event, tournament }: { event: TournamentEvent; tournament: Tournament }) {
  const tournamentFinished = tournament.finishedAt !== null;
  return (
    <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
      <Link to={`/events/${event.id}`} className="micro mb-4 inline-block underline">
        ← {event.name}
      </Link>

      <PageHeader eyebrow={`Organise · ${EVENT_FORMAT_LABEL[event.format]}`} title={event.name} />
      <p className="-mt-3 mb-6 text-sm text-ink-muted">{describeEvent(event)}</p>

      {/* The entry list is not here any more. Reading who is in and taking somebody out are
          the same screen for everybody — an organiser simply sees the notes and the way out
          on it — and having a second copy of the list behind this door meant two lists to
          keep in step. What is left below is what only an organiser can do at all. */}
      <Link to={`/events/${event.id}/entries`} className="micro mb-8 inline-block underline">
        Entries, notes and taking somebody out
      </Link>
      {offersSeeding(event) ? (
        <Link to={`/events/${event.id}/seeding`} className="micro mb-8 ml-4 inline-block underline">
          Seeding{seedingHint(event) ? ` · ${seedingHint(event)}` : ""}
        </Link>
      ) : null}

      <RegistrationControl event={event} tournamentFinished={tournamentFinished} />

      <DetailsEditor event={event} />

      <DescriptionEditor event={event} />

      {event.format === "team_tie" ? <TieTeamsEditor event={event} /> : null}

      {/* A pool's teams come from the auction, so it has no draw; the database refuses it
          fixtures anyway. Its set-up is in its place: the pool's own Squads, Seeding and Lots
          pages, and its auction console. */}
      {event.format === "auction_pool" ? (
        <section className="border-t-2 pt-4" style={{ borderColor: "var(--color-line)" }}>
          <h2 className="micro mb-1">Auction</h2>
          <p className="text-sm text-ink-muted">Teams are picked at the auction.</p>
          <PoolStepRows tournament={tournament} event={event} />
        </section>
      ) : (
        <DrawPanel event={event} />
      )}
    </main>
  );
}

/**
 * Whether the doors are open, and the way to shut or reopen them now.
 *
 * Closing is the state a passed cutoff date makes, reached on demand, so everything that already
 * says "Closed" — the register screen, the event page — follows with no change. It asks twice,
 * because it takes effect for every member at once and replaces any date set below. Withdrawing
 * stays refused once closed, and an organiser can still add and remove people; that is the
 * database's rule and is not repeated here.
 *
 * An event of a finished tournament shows the state and nothing to press: it is not meant to be
 * reopened, and the register screen already offers no way in.
 */
function RegistrationControl({
  event,
  tournamentFinished,
}: {
  event: TournamentEvent;
  tournamentFinished: boolean;
}) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const closed = areEntriesClosed(event);

  const done = () => {
    setConfirming(false);
    void queryClient.invalidateQueries({ queryKey: ["tournaments"] });
  };
  const close = useMutation({
    mutationFn: () => closeEventEntries(event.id),
    onSuccess: done,
    onError: () => setConfirming(false),
  });
  const reopen = useMutation({ mutationFn: () => reopenEventEntries(event.id), onSuccess: done });

  const state = closed
    ? `Registration is closed${event.entriesCloseAt ? ` (since ${closesWhen(event.entriesCloseAt)})` : ""}.`
    : event.entriesCloseAt
      ? `Registration is open until ${closesWhen(event.entriesCloseAt)}.`
      : "Registration is open.";

  return (
    <section className="mb-8 border-t-2 pt-4" style={{ borderColor: "var(--color-line)" }}>
      <h2 className="micro mb-3">Registration</h2>
      <p className="text-[15px] font-semibold" data-testid="registration-state">
        {state}
      </p>

      {tournamentFinished ? null : closed ? (
        <div className="mt-3">
          <Button tone="line" onClick={() => reopen.mutate()} disabled={reopen.isPending}>
            Reopen registration
          </Button>
          <p className="mt-2 text-sm text-ink-muted">
            Leaves no closing date. Set one below to close again later.
          </p>
        </div>
      ) : confirming ? (
        <div className="mt-3">
          <p className="mb-2 text-sm font-semibold">
            Close registration now? {event.entriesCloseAt ? "This replaces the closing date. " : ""}
            Nobody new can register, and nobody can withdraw without asking you.
          </p>
          <div className="flex gap-2">
            <Button tone="primary" onClick={() => close.mutate()} disabled={close.isPending}>
              Yes, close it
            </Button>
            <Button tone="line" onClick={() => setConfirming(false)}>
              Not yet
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-3">
          <Button tone="line" onClick={() => setConfirming(true)}>
            Close registration now
          </Button>
        </div>
      )}

      {close.error || reopen.error ? (
        <div className="mt-3">
          <Notice tone="attention">{(close.error ?? reopen.error)?.message}</Notice>
        </div>
      ) : null}
    </section>
  );
}

/**
 * Name, places, when entries close, rating bounds — the facts an organiser gets wrong at
 * creation and finds out on the night. Category and "counts towards ratings" are not here:
 * the database locks both once somebody has entered or played.
 *
 * Lowering places below the number entered moves the most recent entries to the waitlist
 * (they keep their place in time, so they go to the front of it), and the form says who
 * before anybody presses Save. A drawn event refuses that; the database's sentence says so.
 */
function DetailsEditor({ event }: { event: TournamentEvent }) {
  const queryClient = useQueryClient();
  const { data: fixtures = [] } = useDraw(event.id);
  const drawn = fixtures.length > 0;

  const initial = {
    name: event.name,
    capacity: event.capacity === null ? "" : String(event.capacity),
    closesAt: event.entriesCloseAt ? toLocalInput(event.entriesCloseAt) : "",
    minRating: event.minRating === null ? "" : String(event.minRating),
    maxRating: event.maxRating === null ? "" : String(event.maxRating),
  };
  const [form, setForm] = useState(initial);
  const set = (key: keyof typeof initial) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  const capacity = form.capacity.trim() === "" ? null : Number(form.capacity);
  const registered = registeredEntries(event.entries);
  // Who the database will move, in the order it will choose them: most recent first.
  const moving =
    capacity !== null && registered.length > capacity
      ? [...registered]
          .sort((a, b) => b.requestedAt.localeCompare(a.requestedAt))
          .slice(0, registered.length - capacity)
      : [];

  const valid =
    form.name.trim().length >= 2 &&
    (capacity === null || (Number.isInteger(capacity) && capacity >= 2));
  const changed = JSON.stringify(form) !== JSON.stringify(initial);

  const save = useMutation({
    mutationFn: () =>
      updateEventDetails(event.id, {
        name: form.name,
        capacity,
        // A `datetime-local` value has no zone: it is the organiser's own wall clock.
        entriesCloseAt: form.closesAt ? new Date(form.closesAt).toISOString() : null,
        minRating: form.minRating ? Number(form.minRating) : null,
        maxRating: form.maxRating ? Number(form.maxRating) : null,
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["tournaments"] }),
  });

  return (
    <section className="mb-8 border-t-2 pt-4" style={{ borderColor: "var(--color-line)" }}>
      <h2 className="micro mb-3">Details</h2>

      <Field label="Name">
        <input
          value={form.name}
          onChange={(change) => set("name")(change.target.value)}
          maxLength={80}
          style={inputStyle}
        />
      </Field>

      <Field
        label="Places"
        hint={`Leave blank and everyone who registers is in. ${registered.length} in now.`}
      >
        <input
          type="number"
          min={2}
          value={form.capacity}
          onChange={(change) => set("capacity")(change.target.value)}
          style={inputStyle}
        />
      </Field>

      {moving.length > 0 ? (
        <div className="-mt-3 mb-6">
          <Notice tone="attention">
            {drawn
              ? `The draw is made, so this event can't drop below ${registered.length} places — take somebody out instead.`
              : `${moving.length} ${moving.length === 1 ? "entry moves" : "entries move"} to the front of the waitlist, most recent first: ${moving.map((entry) => entry.displayName).join(", ")}.`}
          </Notice>
        </div>
      ) : null}

      <Field
        label="Entries close"
        hint="Earlier or later, any time. After it, nobody can enter or withdraw themselves — an organiser still can. Blank for no cutoff."
      >
        <input
          type="datetime-local"
          value={form.closesAt}
          onChange={(change) => set("closesAt")(change.target.value)}
          style={inputStyle}
        />
      </Field>

      <div className="flex gap-4">
        <Field label="Min rating" hint="Optional">
          <input
            type="number"
            step="0.1"
            value={form.minRating}
            onChange={(change) => set("minRating")(change.target.value)}
            style={inputStyle}
          />
        </Field>
        <Field label="Max rating" hint="Optional">
          <input
            type="number"
            step="0.1"
            value={form.maxRating}
            onChange={(change) => set("maxRating")(change.target.value)}
            style={inputStyle}
          />
        </Field>
      </div>

      <Button
        tone="primary"
        onClick={() => save.mutate()}
        disabled={!changed || !valid || save.isPending || (drawn && moving.length > 0)}
      >
        {save.isPending ? "Saving…" : "Save details"}
      </Button>
      {save.error ? <Notice tone="attention">{save.error.message}</Notice> : null}
    </section>
  );
}

/** An ISO instant as a `datetime-local` value in the reader's own time zone. */
function toLocalInput(iso: string): string {
  const date = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * What players read on the event page and the register screen. Editable whenever — nothing
 * reads it but a person — so it sits first, as the thing most often corrected ("starts 8pm,
 * not 7"). Save only lights up when there is something different to save.
 */
function DescriptionEditor({ event }: { event: TournamentEvent }) {
  const queryClient = useQueryClient();
  const [text, setText] = useState(event.description ?? "");

  const save = useMutation({
    mutationFn: () => updateEventDescription(event.id, text),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["tournaments"] }),
  });

  const changed = text.trim() !== (event.description ?? "");

  return (
    <section className="mb-8 border-t-2 pt-4" style={{ borderColor: "var(--color-line)" }}>
      <h2 className="micro mb-3">Description</h2>
      <Field label="Shown to players" hint="Start time, what to bring. Leave blank for none.">
        <textarea
          value={text}
          onChange={(change) => setText(change.target.value)}
          style={{ ...inputStyle, minHeight: 88, padding: "12px" }}
          maxLength={1000}
        />
      </Field>
      <Button tone="primary" onClick={() => save.mutate()} disabled={!changed || save.isPending}>
        {save.isPending ? "Saving…" : "Save description"}
      </Button>
      {save.error ? <Notice tone="attention">{save.error.message}</Notice> : null}
    </section>
  );
}

type Side = "A" | "B" | "";
/** Both sides plus the bench, each in the order the organiser put them. */
interface Lineup {
  A: string[];
  B: string[];
  "": string[];
}

/**
 * The two sides of a team tie, and the order of them.
 *
 * This is the one format where the entry list does not determine the draw. Every fixture is
 * seed i of one side against seed i of the other, so **the order within a side is the
 * fixture list**, not a way of displaying the roster — which is why it is arranged here
 * rather than sorted for you.
 *
 * Auto-pick and hand-picking are the same underlying write. Auto exists because it is the
 * right answer most Tuesdays; the arrows exist because the week two players want the top duel
 * and one of them is carrying an injury, no algorithm knows that.
 */
function TieTeamsEditor({ event }: { event: TournamentEvent }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [lineup, setLineup] = useState<Lineup>({ A: [], B: [], "": [] });
  const [names, setNames] = useState({ a: "", b: "" });

  const { data: teams } = useQuery({
    queryKey: ["tie-teams", event.id],
    queryFn: () => fetchTieTeams(event.id),
  });

  const entrants = registeredEntries(event.entries);

  // Only for the automatic split, and only for the people actually entered. A tie is a dozen
  // players, so this is one small request rather than a join the entry list did not need.
  const { data: ratings } = useQuery({
    queryKey: ["ratings", event.id, event.kind],
    queryFn: () =>
      fetchRatings(
        entrants.map((entry) => entry.memberId),
        event.kind,
      ),
    enabled: entrants.length > 0,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["tie-teams", event.id] });

  const save = useMutation({
    mutationFn: (next: Lineup) => setTieTeams(event.id, next.A, next.B, { a: names.a, b: names.b }),
    onSuccess: () => {
      setEditing(false);
      void refresh();
    },
  });

  const sideA = teams?.find((team) => team.side === "A");
  const sideB = teams?.find((team) => team.side === "B");
  const picked = Boolean(sideA?.members.length && sideB?.members.length);
  const roster = rosterOf(event);
  const busy = save.isPending;

  /**
   * Let the app pick: snake down the seeding order, then swap until the two totals stop
   * improving — `splitIntoTeams` in @rally/draw, the same code the demo seed uses and the
   * only implementation of it. It hands back each side already in seed order, which is
   * exactly what `set_tie_teams` now stores.
   */
  const autoSplit = (): Lineup => {
    const seeds = seedEntrants(
      entrants.map((entry) => ({
        memberIds: [entry.memberId],
        rating: ratings?.get(entry.memberId) ?? null,
      })),
    );
    const split = splitIntoTeams(seeds);
    const ids = (side: typeof split.a) => side.map((seed) => seed.memberIds[0] as string);

    return {
      A: ids(split.a),
      B: ids(split.b),
      // The odd one out, plus anybody the organiser had already benched. A mirrored tie
      // cannot halve an odd number and refusing outright would be a button that does nothing.
      "": split.unplaced ? [split.unplaced.memberIds[0] as string] : [],
    };
  };

  const startEditing = () => {
    // Anybody who has left since the sheet was written drops off it here rather than
    // appearing as a nameless row that cannot be saved. The side comes up one short and the
    // warning below says so, which is the thing the organiser has to act on.
    const entered = new Set(entrants.map((entry) => entry.memberId));
    const onSide = (team: typeof sideA) =>
      (team?.members ?? []).map((m) => m.memberId).filter((id) => entered.has(id));

    const assigned = new Set([...onSide(sideA), ...onSide(sideB)]);
    setLineup({
      A: onSide(sideA),
      B: onSide(sideB),
      "": entrants.filter((e) => !assigned.has(e.memberId)).map((e) => e.memberId),
    });
    setNames({ a: sideA?.name ?? "", b: sideB?.name ?? "" });
    setEditing(true);
  };

  /** Move one player to a side, always onto the end — the arrows are for position. */
  const moveTo = (memberId: string, to: Side) => {
    setLineup((current) => {
      const next: Lineup = {
        A: current.A.filter((id) => id !== memberId),
        B: current.B.filter((id) => id !== memberId),
        "": current[""].filter((id) => id !== memberId),
      };
      next[to] = [...next[to], memberId];
      return next;
    });
  };

  /** Swap with the neighbour, which is what changes who plays whom. */
  const shift = (side: Side, index: number, by: -1 | 1) => {
    setLineup((current) => {
      const list = [...current[side]];
      const target = index + by;
      if (target < 0 || target >= list.length) return current;
      [list[index], list[target]] = [list[target] as string, list[index] as string];
      return { ...current, [side]: list };
    });
  };

  const even = lineup.A.length === lineup.B.length;

  return (
    <section className="mb-8 border-t-2 pt-4" style={{ borderColor: "var(--color-line)" }}>
      <h2 className="micro mb-3">Teams</h2>

      {editing ? (
        <>
          <div className="mb-4 flex gap-4">
            <Field label={`Side A · ${lineup.A.length}`}>
              <input
                value={names.a}
                onChange={(e) => setNames({ ...names, a: e.target.value })}
                placeholder="Side A"
                style={inputStyle}
                maxLength={40}
              />
            </Field>
            <Field label={`Side B · ${lineup.B.length}`}>
              <input
                value={names.b}
                onChange={(e) => setNames({ ...names, b: e.target.value })}
                placeholder="Side B"
                style={inputStyle}
                maxLength={40}
              />
            </Field>
          </div>

          <SideEditor
            side="A"
            label={names.a.trim() || "Side A"}
            other={names.b.trim() || "Side B"}
            ids={lineup.A}
            roster={roster}
            onMove={moveTo}
            onShift={shift}
          />
          <SideEditor
            side="B"
            label={names.b.trim() || "Side B"}
            other={names.a.trim() || "Side A"}
            ids={lineup.B}
            roster={roster}
            onMove={moveTo}
            onShift={shift}
          />

          {lineup[""].length > 0 ? (
            <>
              <p className="micro mt-5">Not playing</p>
              {lineup[""].map((id) => (
                <div
                  key={id}
                  className="flex items-center justify-between gap-3 border-t py-2"
                  style={{ borderColor: "var(--color-line-muted)" }}
                >
                  <span className="text-sm">{roster.get(id)?.name ?? "someone"}</span>
                  <span className="flex gap-1">
                    <Button
                      tone="quiet"
                      onClick={() => moveTo(id, "A")}
                      style={{ minHeight: 36, fontSize: 13 }}
                    >
                      → {names.a.trim() || "A"}
                    </Button>
                    <Button
                      tone="quiet"
                      onClick={() => moveTo(id, "B")}
                      style={{ minHeight: 36, fontSize: 13 }}
                    >
                      → {names.b.trim() || "B"}
                    </Button>
                  </span>
                </div>
              ))}
            </>
          ) : null}

          {/* Said before Save is pressed rather than after. The database refuses unequal
              sides, and being told why by a red line under a button you already tapped is
              the worse version of the same information. */}
          {!even ? (
            <p className="micro mt-3" style={{ color: "var(--color-attention)" }}>
              A mirrored tie needs equal sides — {lineup.A.length} against {lineup.B.length}.
            </p>
          ) : null}

          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              tone="primary"
              onClick={() => save.mutate(lineup)}
              disabled={busy || !even || lineup.A.length < 2}
            >
              {save.isPending ? "Saving…" : "Save teams"}
            </Button>
            <Button onClick={() => setLineup(autoSplit())} disabled={busy}>
              Even them up
            </Button>
            <Button onClick={() => setEditing(false)} disabled={busy}>
              Cancel
            </Button>
          </div>
        </>
      ) : (
        <>
          {picked ? (
            <TeamSheets teams={teams ?? []} roster={roster} />
          ) : (
            <p className="text-sm text-ink-muted">
              No teams yet. Every fixture is a pair from one side against the same seeds on the
              other, so the draw needs these first.
            </p>
          )}

          <div className="mt-3 flex flex-wrap gap-2">
            <Button onClick={startEditing} disabled={busy}>
              {picked ? "Edit teams" : "Pick teams"}
            </Button>
            {!picked ? (
              <Button tone="primary" onClick={() => save.mutate(autoSplit())} disabled={busy}>
                {save.isPending ? "Splitting…" : "Pick for me"}
              </Button>
            ) : null}
          </div>
        </>
      )}

      {save.error ? (
        <p className="mt-2 text-sm" style={{ color: "var(--color-attention)" }}>
          {save.error.message}
        </p>
      ) : null}
    </section>
  );
}

/**
 * One side, in seed order, with the controls to change it.
 *
 * The number down the left is the seed, and it is the whole reason this screen exists: seed
 * 1 here plays seed 1 there. Arrows rather than drag and drop — a drag target on a phone is
 * a fight, and an ordering of eight people is a few taps either way.
 */
function SideEditor({
  side,
  label,
  other,
  ids,
  roster,
  onMove,
  onShift,
}: {
  side: Side;
  label: string;
  other: string;
  ids: string[];
  roster: Roster;
  onMove: (memberId: string, to: Side) => void;
  onShift: (side: Side, index: number, by: -1 | 1) => void;
}) {
  return (
    <div className="mb-5">
      <p className="micro">
        {label} · {ids.length}
      </p>
      {ids.length === 0 ? <p className="mt-2 text-sm text-ink-muted">Nobody yet.</p> : null}

      {ids.map((id, index) => (
        <div
          key={id}
          className="flex items-center justify-between gap-2 border-t py-2"
          style={{ borderColor: "var(--color-line-muted)" }}
        >
          <span className="flex min-w-0 items-baseline gap-2">
            <span className="micro">{index + 1}</span>
            <span className="truncate text-sm">{roster.get(id)?.name ?? "someone"}</span>
          </span>

          <span className="flex shrink-0 items-center">
            <IconButton
              label={`Move ${roster.get(id)?.name ?? "player"} up`}
              disabled={index === 0}
              onClick={() => onShift(side, index, -1)}
            >
              <IconUp />
            </IconButton>
            <IconButton
              label={`Move ${roster.get(id)?.name ?? "player"} down`}
              disabled={index === ids.length - 1}
              onClick={() => onShift(side, index, 1)}
            >
              <IconDown />
            </IconButton>
            <Button
              tone="quiet"
              onClick={() => onMove(id, side === "A" ? "B" : "A")}
              style={{ minHeight: 36, fontSize: 13 }}
            >
              → {other}
            </Button>
            <Button
              tone="quiet"
              onClick={() => onMove(id, "")}
              style={{ minHeight: 36, fontSize: 13 }}
            >
              Bench
            </Button>
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Propose a draw, read it, keep it or throw it away.
 *
 * Two steps on purpose. §5.4: "Always show the resulting team ratings before confirming.
 * Players accept an algorithm they can audit." So the preview computes the whole draw,
 * writes none of it, and hands back the teams it would make.
 */
function DrawPanel({ event }: { event: TournamentEvent }) {
  const queryClient = useQueryClient();
  // Open by default, because once a draw exists this list is the evening: it is where the
  // scores go in. It was hidden when the only thing it could do was be read.
  const [open, setOpen] = useState(true);
  const [perPlayer, setPerPlayer] = useState<number | null>(event.matchesPerPlayer);
  const [tiePairing, setTiePairing] = useState<TiePairing>(event.tiePairing);
  const navigate = useNavigate();

  const { data: fixtures } = useQuery({
    queryKey: ["draw", event.id],
    queryFn: () => fetchDraw(event.id),
  });

  const { data: teams } = useQuery({
    queryKey: ["tie-teams", event.id],
    queryFn: () => fetchTieTeams(event.id),
    enabled: event.format === "team_tie",
  });

  // Only a tie has a pairing to send. Every other format would ignore it, but sending it
  // anyway would put a word in the request that means nothing for them.
  const pairing = event.format === "team_tie" ? tiePairing : undefined;

  const preview = useMutation({
    mutationFn: () =>
      generateDraw(event.id, { preview: true, matchesPerPlayer: perPlayer ?? undefined, pairing }),
  });

  const commit = useMutation({
    mutationFn: (randomSeed: number) =>
      generateDraw(event.id, { randomSeed, matchesPerPlayer: perPlayer ?? undefined, pairing }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["draw", event.id] });
      // The committed m is written back to the event, so the summary line and the form both
      // have to be re-read rather than left describing the previous draw.
      void queryClient.invalidateQueries({ queryKey: ["tournaments"] });
      preview.reset();
      setOpen(true);
    },
  });

  const drawn = fixtures?.length ?? 0;
  const played = fixtures?.filter((fixture) => fixture.result).length ?? 0;
  /** Mexicano and Swiss: drawn a round at a time, from the table, rather than whole. */
  const swiss = event.format === "swiss";
  const rounds = event.format === "mexicano" || swiss;
  const roundsPlayed = fixtures?.reduce((most, fixture) => Math.max(most, fixture.round), 0) ?? 0;
  // Matches still to play. A Swiss bye is a fixture that is never played, so counting fixtures
  // without a result would hold the next round up for ever.
  const waiting = fixtures?.filter(isPlayable).length ?? 0;
  // Every round the organiser asked for is drawn. More can still be had by raising the count.
  const allDrawn = swiss && perPlayer != null && roundsPlayed >= perPlayer;
  const proposed = preview.data;
  const busy = preview.isPending || commit.isPending;
  const failure = preview.error ?? commit.error;
  const roster = rosterOf(event);

  // The tie's team size, which is what decides the legal values of m. Both sides are equal
  // by the time they are stored, so either one answers it.
  const teamSize = teams?.find((team) => team.side === "A")?.members.length ?? 0;
  /** An americano's `m` is against the field rather than a team sheet. */
  const entered = registeredEntries(event.entries).length;

  return (
    <section className="border-t-2 pt-4" style={{ borderColor: "var(--color-line)" }}>
      <h2 className="micro mb-3">Draw</h2>

      {/* The two formats where how long the evening runs is a decision rather than a
          consequence of the entry list. A tie's `m` is per side; an americano's is over the
          whole field. */}
      {event.format === "team_tie" && teamSize >= 2 ? (
        <>
          <div className="mb-4">
            <Selector
              label="Pairing"
              value={tiePairing}
              onChange={(next) => {
                setTiePairing(next as TiePairing);
                // A preview drawn the other way is not the draw this button would save.
                preview.reset();
              }}
              options={[
                { value: "mirrored", label: "Mirrored — same seeds meet" },
                { value: "open", label: "Open — any close pair meets" },
              ]}
            />
            <p className="mt-2 text-sm text-ink-muted">
              {tiePairing === "open"
                ? `Any pair can meet a pair within ${OPEN_SEED_GAP} on seed total, so opponents vary and each player can play up to ${2 * (teamSize - 1)}.`
                : `Each pair plays the same seeds on the other side. Up to ${teamSize - 1} each.`}
            </p>
          </div>
          <MatchesPerPlayer
            format={event.format}
            size={teamSize}
            value={perPlayer}
            onChange={setPerPlayer}
            pairing={tiePairing}
          />
        </>
      ) : null}
      {swiss && entered >= 2 ? (
        <SwissRounds
          entrants={entered}
          drawn={roundsPlayed}
          stored={event.matchesPerPlayer}
          value={perPlayer}
          onChange={(next) => {
            setPerPlayer(next);
            preview.reset();
          }}
        />
      ) : null}
      {event.format === "americano" && entered >= 4 ? (
        <MatchesPerPlayer
          format={event.format}
          size={entered}
          value={perPlayer}
          onChange={setPerPlayer}
        />
      ) : null}

      {/* A mexicano is not drawn, it is drawn *up to here*: each round's pairings come from
          the table the last one left, so the button adds one round rather than replacing an
          evening. Saying "Redraw" on it would offer to throw away results it cannot throw
          away. */}
      {rounds ? (
        <p className="mb-3 text-sm text-ink-muted">
          {drawn === 0
            ? swiss
              ? "A Swiss event is drawn one round at a time. Round 1 is the seeding order, top half against bottom half; after that players on the same wins meet, top half against bottom half again, and nobody plays the same person twice."
              : "A mexicano is drawn one round at a time. Round 1 comes from the ratings; after that the leader partners the fourth-placed player against the second and third, straight down the table."
            : waiting > 0
              ? "The round on court has to finish before the next one can be drawn — it is the table that decides who plays whom."
              : allDrawn
                ? `All ${perPlayer} rounds are drawn and played. The table is final unless you raise the rounds above.`
                : "Every match so far has a score, so the next round can be drawn from the table."}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        {!proposed && !allDrawn ? (
          <Button onClick={() => preview.mutate()} disabled={busy || (rounds && waiting > 0)}>
            {preview.isPending
              ? "Working…"
              : swiss && perPlayer != null
                ? `Draw round ${roundsPlayed + 1} of ${perPlayer}`
                : rounds
                  ? `Draw round ${roundsPlayed + 1}`
                  : drawn > 0
                    ? "Redraw"
                    : "Generate draw"}
          </Button>
        ) : null}

        {drawn > 0 ? (
          <Button tone="quiet" onClick={() => setOpen(!open)} disabled={busy}>
            {open ? "Hide draw" : `Show draw (${drawn})`}
          </Button>
        ) : null}
      </div>

      {drawn > 0 ? (
        <p className="micro mt-2">
          {/* Byes are neither played nor waiting, so they are not in the count. */}
          {played} of {played + waiting} played
          {allDrawn ? ` · all ${perPlayer} rounds drawn` : ""}
        </p>
      ) : null}

      {drawn === 0 && !proposed ? (
        <p className="mt-2 text-sm text-ink-muted">
          Nothing drawn yet. Generating shows you the draw before anything is saved.
        </p>
      ) : null}

      {proposed ? (
        <div
          className="mt-3 border-t-2 pt-3"
          style={{ borderColor: "var(--color-line)" }}
          data-testid="draw-preview"
        >
          <p className="micro">
            {proposed.round
              ? `Round ${proposed.round}${proposed.of ? ` of ${proposed.of}` : ""} — nothing is saved yet`
              : drawn > 0
                ? "This would replace the current draw"
                : "Nothing is saved yet"}
            {proposed.round ? " · " : " — "}
            {proposed.fixtureCount} match{proposed.fixtureCount === 1 ? "" : "es"}
            {proposed.round
              ? ""
              : ` over ${proposed.rounds} round${proposed.rounds === 1 ? "" : "s"}`}
            {proposed.strategy ? ` · ${PAIRING_LABEL[proposed.strategy].toLowerCase()}` : ""}
            {proposed.tiePairing === "open"
              ? ` · open · largest gap ${proposed.largestSeedGap ?? 0}`
              : proposed.tiePairing === "mirrored"
                ? " · mirrored"
                : ""}
          </p>

          {/* Who is off, by name. "2 sitting out" is a number somebody then has to work out
              the answer to, and the answer is the thing they were going to say out loud. */}
          {proposed.bye ? (
            <p className="micro mt-2">Bye: {roster.get(proposed.bye)?.name ?? "someone"}</p>
          ) : null}

          {/* Only when no round without one exists. Named, because the two people are the ones
              who will ask why they are playing each other again. */}
          {proposed.rematches?.length ? (
            <p className="micro mt-2" style={{ color: "var(--color-attention)" }}>
              No round without a rematch exists, so{" "}
              {proposed.rematches
                .map(
                  ([a, b]) =>
                    `${roster.get(a)?.name ?? "someone"} and ${roster.get(b)?.name ?? "someone"}`,
                )
                .join(", ")}{" "}
              meet again.
            </p>
          ) : null}

          {proposed.sittingOut?.length ? (
            <p className="micro mt-2">
              Sitting out:{" "}
              {proposed.sittingOut.map((id) => roster.get(id)?.name ?? "someone").join(", ")}
            </p>
          ) : null}

          {/* The audit §5.4 asks for. Seeing the pair ratings side by side is what makes a
              pairing arguable-with rather than something the app did to you. */}
          {proposed.pairs.length > 0 ? (
            <ul className="mt-2">
              {proposed.pairs.map((pair) => (
                <li
                  key={pair.names.join("+")}
                  className="flex items-baseline justify-between gap-3 border-t py-1.5"
                  style={{ borderColor: "var(--color-line-muted)" }}
                >
                  <span className="text-sm">{pair.names.join(" & ")}</span>
                  <span className="micro">
                    {pair.rating == null ? "unrated" : formatRating(pair.rating)}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}

          {proposed.unrated > 0 ? (
            <p className="micro mt-2">
              {proposed.unrated} {proposed.pairs.length > 0 ? "pair" : "entrant"}
              {proposed.unrated === 1 ? "" : "s"} had no {event.kind} rating to go on and{" "}
              {proposed.unrated === 1 ? "was" : "were"} seeded last.
            </p>
          ) : null}

          {proposed.restViolations > 0 ? (
            <p className="micro mt-2" style={{ color: "var(--color-attention)" }}>
              {proposed.restViolations} match{proposed.restViolations === 1 ? "" : "es"} start
              {proposed.restViolations === 1 ? "s" : ""} with someone straight off the previous one.
              At this many matches each there is no order that avoids it — drop to fewer per player
              for a cleaner run.
            </p>
          ) : null}

          <FixtureList fixtures={proposed.fixtures} roster={roster} />

          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              tone="primary"
              onClick={() => commit.mutate(proposed.randomSeed)}
              disabled={busy}
            >
              {commit.isPending ? "Saving…" : rounds ? "Put this round on" : "Use this draw"}
            </Button>
            {/* Only random can produce a different answer from the same entrants, so only
                random gets a reroll. Offering it for snake would be a button that does
                nothing, twice. */}
            {proposed.strategy === "random" ? (
              <Button onClick={() => preview.mutate()} disabled={busy}>
                Shuffle again
              </Button>
            ) : null}
            <Button onClick={() => preview.reset()} disabled={busy}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      {failure ? (
        <p className="mt-2 text-sm" style={{ color: "var(--color-attention)" }}>
          {failure.message}
        </p>
      ) : null}

      {/* Scoring lives on its own screen. An organiser scores any fixture, not only their
          own — they are usually the one holding the clipboard for players who never open the
          app — but the pad has no business sitting under a Redraw button. */}
      {open && fixtures ? (
        <FixtureList
          fixtures={fixtures}
          roster={roster}
          canScore={isPlayable}
          onScore={(fixture) => navigate(`/events/${event.id}/score/${fixture.id}`)}
          onEdit={(fixture) => navigate(`/events/${event.id}/fixture/${fixture.id}`)}
        />
      ) : null}

      {/* Adding one match is the small version of redrawing, and it is the one that does not
          throw away the results already in. Absent for a knockout, where the bracket decides
          who plays whom after round one. */}
      {open && drawn > 0 && event.format !== "knockout" ? (
        <Link to={`/events/${event.id}/fixture/new`} className="micro mt-4 inline-block underline">
          Add a match
        </Link>
      ) : null}
    </section>
  );
}

/**
 * How many matches each player of a tie gets — the `m` of docs/formats/team-tie.md.
 *
 * Offered here, at the draw, rather than only on the event form, because it is a decision
 * about how long the evening runs: it depends on who turned up and how late the hall is
 * booked, neither of which is known when somebody first types the event's name.
 *
 * Only the legal values are offered, and that is not tidiness. `n × m` has to be even —
 * every fixture uses two of the slots — so five a side can play 2 each or 4 each and never
 * 3. An organiser typing 3 there deserves the neighbouring options rather than an error
 * about parity after the fact.
 */
function MatchesPerPlayer({
  format,
  size,
  value,
  onChange,
  pairing = "mirrored",
}: {
  format: EventFormat;
  /** A tie's team size, or an americano's whole field. */
  size: number;
  value: number | null;
  onChange: (next: number) => void;
  /** A tie's pairing, which decides how far `m` can go. */
  pairing?: TiePairing;
}) {
  const tie = format === "team_tie";
  const legal = tie ? validMatchesPerPlayer(size, pairing) : validAmericanoMatches(size);
  if (legal.length === 0) return null;

  // A tie defaults to everyone partnering everyone once — the format at its fullest when
  // Mirrored, and where Open stops short of repeating partners. Switching to Open does not
  // double the evening unasked. An americano's maximum is a fixture list nobody has the hall
  // for: twelve players on one court, eleven matches each, is thirty-three matches. Four
  // each is an evening, and the selector is right there.
  const fallback = tie
    ? (legal.filter((m) => m <= size - 1).pop() ?? (legal[0] as number))
    : (legal.filter((m) => m <= 4).pop() ?? (legal[0] as number));
  const chosen = value != null && legal.includes(value) ? value : fallback;

  // Tell the parent what is on screen. Without this the selector shows a default the draw
  // never receives: an event with no `m` stored sent none, and the Edge Function refused
  // with "needs matches per player set" while the number sat right there in the dropdown.
  useEffect(() => {
    if (value !== chosen) onChange(chosen);
  }, [value, chosen, onChange]);

  return (
    <div className="mb-4">
      <Selector
        label="Matches each"
        value={String(chosen)}
        onChange={(next) => onChange(Number(next))}
        options={legal.map((m) => ({
          value: String(m),
          label: `${m} match${m === 1 ? "" : "es"} each`,
        }))}
      />
      <p className="mt-2 text-sm text-ink-muted">
        {tie
          ? `${size} a side · ${fixtureCount(size, chosen)} matches in the tie`
          : `${size} players · ${americanoFixtureCount(size, chosen)} matches, ` +
            `${Math.max(0, size - 4)} sitting out at a time`}
        {tie && chosen === size - 1
          ? " · everyone partners everyone, which leaves no order without someone playing twice running"
          : tie && chosen > size - 1
            ? " · some partners play together twice"
            : ""}
      </p>
    </div>
  );
}

/**
 * How many rounds a Swiss event plays. Asked when drawing rather than only on the event form,
 * like matches each: it is a decision about how long the day runs, and it can change between
 * rounds — never below the rounds already drawn, never past the field minus one.
 */
function SwissRounds({
  entrants,
  drawn,
  stored,
  value,
  onChange,
}: {
  entrants: number;
  drawn: number;
  stored: number | null;
  value: number | null;
  onChange: (next: number) => void;
}) {
  const choices = swissRoundChoices(entrants, drawn);
  const chosen =
    value != null && choices.includes(value) ? value : swissRoundDefault(entrants, drawn, stored);

  // The same reason as matches each: the number on screen has to be the number sent.
  useEffect(() => {
    if (chosen != null && value !== chosen) onChange(chosen);
  }, [value, chosen, onChange]);

  if (chosen == null) return null;

  return (
    <div className="mb-4">
      <Selector
        label="Rounds"
        value={String(chosen)}
        onChange={(next) => onChange(Number(next))}
        options={choices.map((r) => ({
          value: String(r),
          label: `${r} round${r === 1 ? "" : "s"}`,
        }))}
      />
      <p className="mt-2 text-sm text-ink-muted">
        {entrants} players · {drawn > 0 ? `${drawn} drawn so far · ` : ""}
        {suggestedSwissRounds(entrants)} rounds separate a single leader
      </p>
    </div>
  );
}
